import {
    CalendarOutlined,
    CloudUploadOutlined,
    DollarOutlined,
    ExportOutlined,
    WalletOutlined
} from '@ant-design/icons'
import { categories, useChannels, useChannelStore } from '@entities/channel'
import { useStatisticsInfo } from '@entities/statistic-info'
import { EDisplayPosts } from '@pages/scheduled-posts-page/ui/scheduled-posts-page'
import useMobileView from '@shared/hooks/isMobile'
import { useClickOutside } from '@shared/hooks/use-click-outside'
import { dayjs } from '@shared/lib/dayjs/dayjs'
import {
    Avatar,
    Card,
    Col,
    DatePicker,
    Divider,
    Flex,
    Popover,
    Radio,
    Row,
    Select,
    Space,
    theme,
    Typography
} from 'antd'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HighchartsMoreModule from 'highcharts/highcharts-more'
import ExportingModule from 'highcharts/modules/exporting'
import SolidGaugeModule from 'highcharts/modules/solid-gauge'
import { useState } from 'react'

// универсальный хелпер
function initModule(mod: any) {
    const fn = mod?.default || mod
    if (typeof fn === 'function') {
        fn(Highcharts)
    }
}

// инициализация
initModule(ExportingModule)
initModule(HighchartsMoreModule)
initModule(SolidGaugeModule)

import { BottomNavMobile } from '@widgets/page-template/ui/bottom-nav-mobile'
import { HorizontalBar } from './HorisontalBar'
import { Involvement } from './Involvement'
import { PostViews } from './PostsViews'
import './styled.scss'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

type DataPoint = {
    createdAt: string
    membersCount: number
}

function aggregateData(data: DataPoint[], mode: EDisplayPosts): DataPoint[] {
    const groupedData: Record<string, DataPoint> = {}

    data.forEach((point) => {
        const dateKey = dayjs(point.createdAt)
            .startOf(
                mode === EDisplayPosts.day
                    ? 'day'
                    : mode === EDisplayPosts.week
                        ? 'week'
                        : mode === EDisplayPosts.month
                            ? 'month'
                            : 'month'
            )
            .toISOString()

        if (!groupedData[dateKey] || dayjs(point.createdAt).isAfter(groupedData[dateKey].createdAt)) {
            groupedData[dateKey] = point
        }
    })

    return Object.values(groupedData)
}

export const StatisticsPage = () => {
    const { current } = useChannelStore()
    const {
        channels,
        actions: { setCurrentChannel },
        channelDetails
    } = useChannels()
    const isMobile = useMobileView()

    const handleChange = (id: number) => {
        setCurrentChannel(channels?.find((c) => c.id === id) ?? null)
    }

    const {
        statisticsInfo,
        statisticsInfoSubscribers,
        dates,
        actions: { setDates }
    } = useStatisticsInfo(current?.id)

    const {
        token: { colorSuccess, colorWhite, colorError, colorBgElevated, colorText, colorHighlight }
    } = theme.useToken()

    const [display, setDisplay] = useState<EDisplayPosts>(EDisplayPosts.week)
    // TODO пока комментим
    // const data = statisticsInfoSubscribers?.subscribersDynamics || []

    // const aggregatedStatistics = display === EDisplayPosts.day ? data : aggregateData(data, display)

    // const chartData = aggregatedStatistics?.map((item) => [new Date(item.createdAt).getTime(), item.membersCount])

    const chartData2 = {
        subscribers:
            statisticsInfoSubscribers?.subscribersDynamics?.map((item) => ({
                x: new Date(item.createdAt).getTime(),
                y: item.membersCount,
                date: item.createdAt
            })) || [],
        unsubscribers:
            statisticsInfoSubscribers?.subscribersLeftDynamics?.map((item) => ({
                x: new Date(item.createdAt).getTime(),
                y: item.membersCount,
                date: item.createdAt
            })) || []
    }

    // Настройка форматов для разных режимов
    const getDateTimeFormats = (mode: EDisplayPosts) => {
        switch (mode) {
            case EDisplayPosts.day:
                return {
                    labelsFormat: '{value:%H:%M}', // Часы:Минуты
                    tooltipFormat: '%d.%m.%Y %H:%M', // Дата и время
                    tickInterval: 3600 * 1000 // Интервал меток: 1 час
                }
            case EDisplayPosts.week:
                return {
                    labelsFormat: '{value:%d.%m}', // День.Месяц
                    tooltipFormat: '%d.%m.%Y', // Дата
                    tickInterval: 24 * 3600 * 1000 // Интервал меток: 1 день
                }
            case EDisplayPosts.month:
                return {
                    labelsFormat: '{value:%d.%m}', // День.Месяц
                    tooltipFormat: '%d.%m.%Y', // Дата
                    tickInterval: 7 * 24 * 3600 * 1000 // Интервал меток: 1 неделя
                }
            default:
                return {
                    labelsFormat: '{value:%d.%m.%Y}', // Полная дата
                    tooltipFormat: '%d.%m.%Y', // Дата
                    tickInterval: null // Автоматический расчет
                }
        }
    }

    const { labelsFormat, tooltipFormat, tickInterval } = getDateTimeFormats(display)

    const minDate = dates.startDate.getTime() // Начало выбранного периода
    const maxDate = dates.endDate.getTime() // Конец выбранного периода

    const options = {
        exporting: {
            enabled: false
        },
        chart: {
            backgroundColor: colorBgElevated,
            borderRadius: 10
        },
        title: {
            text: 'Динамика подписчиков',
            style: {
                color: colorText
            }
        },
        xAxis: {
            type: 'datetime',
            lineColor: colorText,
            labels: {
                style: {
                    color: colorText
                },
                format: labelsFormat
            },
            tickColor: colorText,
            tickInterval: tickInterval,
            min: minDate,
            max: maxDate
        },
        yAxis: {
            lineColor: colorText,
            gridLineWidth: 0,
            labels: {
                style: {
                    color: colorText
                }
            },
            title: {
                style: {
                    color: colorText
                },
                text: 'Количество подписчиков'
            },
            allowDecimals: false
        },
        tooltip: {
            backgroundColor: colorBgElevated,
            style: {
                color: colorText
            },
            formatter: function () {
                return `<b>${this.series.name}:</b> ${this.y}`
            }
        },
        legend: {
            itemStyle: {
                color: colorText
            },
            itemHoverStyle: {
                color: colorText
            }
        },
        series: [
            {
                name: 'Подписки',
                data: chartData2.subscribers,
                type: 'line',
                color: colorSuccess
            },
            {
                name: 'Отписки',
                data: chartData2.unsubscribers,
                type: 'line',
                color: colorError
            }
        ],
        credits: {
            enabled: false
        }
    }

    const postsData = statisticsInfo?.postsStatistics?.postsCount || []

    const postsSeries = postsData.map((item) => ({
        x: new Date(item.date).getTime(),
        y: item.count
    }))

    const opn = {
        exporting: {
            enabled: false
        },
        chart: {
            type: 'column',
            backgroundColor: colorBgElevated,
            borderRadius: 10
        },
        title: {
            text: 'Количество публикаций',
            style: { color: colorText }
        },
        xAxis: {
            type: 'datetime',
            lineColor: colorText,
            labels: {
                style: { color: colorText },
                format: labelsFormat
            },
            tickColor: colorText,
            tickInterval: tickInterval,
            min: dates.startDate.getTime(),
            max: dates.endDate.getTime()
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Публикации',
                style: { color: colorText }
            },
            labels: {
                style: { color: colorText }
            },
            allowDecimals: false
        },
        tooltip: {
            backgroundColor: colorBgElevated,
            style: { color: colorText },
            xDateFormat: tooltipFormat,
            pointFormat: '<b>{series.name}:</b> {point.y}'
        },
        plotOptions: {
            column: {
                pointPadding: 0.2,
                borderWidth: 0
            }
        },
        series: [
            {
                name: 'Публикации',
                data: postsSeries,
                color: colorHighlight
            }
        ],
        credits: {
            enabled: false
        }
    }

    const [isPeriodVisible, setIsPeriodVisible] = useState(false)

    const ref = useClickOutside<HTMLDivElement>((event) => {
        const target = event.target as HTMLElement
        if (!target.closest('.ant-picker-dropdown')) {
            setIsPeriodVisible(false)
        }
    })

    const periodPicker = (
        <div ref={ref}>
            <RangePicker
                onChange={(value) => {
                    setDates({
                        startDate: value?.[0]?.toDate() || new Date(),
                        endDate: value?.[1]?.toDate() || new Date()!
                    })
                }}
            />
        </div>
    )

    if (isMobile) {
        return (
            <>
                <Card className={'mobile-settings'} size="small" style={{ flex: 1, overflow: 'auto', borderRadius: 0 }}>
                    <Card size="small" style={{ backgroundColor: colorWhite }}>
                        {/* Данные канала */}
                        <Flex justify={'space-between'} align="start">
                            <Flex vertical={true} gap={6} style={{ width: 200 }}>
                                <Title
                                    level={4}
                                    style={{ margin: 0, cursor: 'pointer' }}
                                    onClick={() => window.open(`https://t.me/${channelDetails?.username}`)}
                                    italic
                                >
                                    @{channelDetails?.name}
                                </Title>

                                <Text>Правообладатель: @{channelDetails?.owner}</Text>
                                <Space size={2}>
                                    <Text>{channelDetails?.timezone}</Text>
                                </Space>
                                <Text>{channelDetails?.membersCount} подписчиков</Text>
                                <Text>{categories.find((c) => c.value === channelDetails?.category)?.label}</Text>
                            </Flex>
                            <Avatar shape="circle" size={50} src={channelDetails?.photoUrl} />
                        </Flex>
                    </Card>
                    <Select
                        style={{
                            border: 'none',
                            width: '100%',
                            marginTop: 21,
                            marginBottom: 10,
                            paddingTop: 0,
                            paddingBottom: 0,
                            height: 50,
                            backgroundColor: '#FEFEFF',
                            borderRadius: 12
                        }}
                        defaultValue={channelDetails?.id}
                        options={channels?.map((item) => ({
                            value: item.id,
                            label: item.name
                        }))}
                        onChange={handleChange}
                    />

                    <Flex gap={'middle'} style={{ marginBottom: 20 }} justify={'space-between'}>
                        <DatePicker
                            showSecond={false}
                            style={{
                                width: '100%',
                                backgroundColor: colorWhite,
                                border: 'none',
                                height: '50px',
                                fontSize: 17
                            }}
                            onChange={(value) => {
                                setDates({
                                    startDate: value.toDate() || new Date(),
                                    endDate: dates.endDate
                                })
                            }}
                            format={['DD/MM/YYYY HH:mm', 'DD/MM/YY HH:mm', 'DD.MM.YYYY HH:mm', 'DD.MM.YY HH:mm']}
                            placeholder="от"
                        />
                        <DatePicker
                            showSecond={false}
                            onChange={(value) => {
                                setDates({
                                    startDate: dates.startDate,
                                    endDate: value.toDate() || new Date()
                                })
                            }}
                            style={{
                                width: '100%',
                                backgroundColor: colorWhite,
                                border: 'none',
                                height: '50px',
                                fontSize: 17
                            }}
                            format={['DD/MM/YYYY HH:mm', 'DD/MM/YY HH:mm', 'DD.MM.YYYY HH:mm', 'DD.MM.YY HH:mm']}
                            placeholder="до"
                        />
                    </Flex>
                    <Flex gap={8} justify={'space-between'} style={{ marginBottom: 25 }}>
                        {/* TODO пока нет, добавить потом */}
                        {/* <Flex
                        style={{
                            backgroundColor: colorHighlight,
                            borderRadius: '8px',
                            width: '100%',
                            position: 'relative'
                        }}
                        vertical
                        align="center"
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: -10,
                                border: '1px solid #7C9BAD',
                                backgroundColor: 'white',
                                borderRadius: 5,
                                padding: 5,
                                display: 'flex'
                            }}
                        >
                            <StarOutlined style={{ fontSize: 20, color: '#7C9BAD' }} />
                        </div>
                        <Title style={{ marginTop: 25, marginBottom: 0 }} level={5}>
                            Партнерские связки
                        </Title>
                        <Text strong style={{ color: colorSuccess, fontSize: 40 }}>
                            {statisticsInfo?.postsStatistics?.relationsCount}
                        </Text>
                    </Flex> */}
                    </Flex>
                    <Flex gap={8} justify={'space-between'} style={{ marginBottom: 25 }}>
                        <Flex
                            style={{
                                backgroundColor: colorHighlight,
                                borderRadius: '8px',
                                width: '100%',
                                position: 'relative'
                            }}
                            vertical
                            align="center"
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: -10,
                                    border: '1px solid #7C9BAD',
                                    backgroundColor: 'white',
                                    borderRadius: 5,
                                    padding: 5,
                                    display: 'flex'
                                }}
                            >
                                <CloudUploadOutlined style={{ fontSize: 20, color: '#7C9BAD' }} />
                            </div>
                            <Title style={{ marginTop: 25, marginBottom: 0 }} level={5}>
                                Опубликовано
                            </Title>
                            <Text strong style={{ color: colorSuccess, fontSize: 40 }}>
                                {statisticsInfo?.postsStatistics?.publishedPosts}
                            </Text>
                        </Flex>
                        <Flex
                            style={{
                                backgroundColor: colorHighlight,
                                borderRadius: '8px',
                                width: '100%',
                                position: 'relative'
                            }}
                            vertical
                            align="center"
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: -10,
                                    border: '1px solid #7C9BAD',
                                    backgroundColor: 'white',
                                    borderRadius: 5,
                                    padding: 5,
                                    display: 'flex'
                                }}
                            >
                                <CalendarOutlined style={{ fontSize: 20, color: '#7C9BAD' }} />
                            </div>
                            <Title style={{ marginTop: 25, marginBottom: 0 }} level={5}>
                                Запланировано
                            </Title>
                            <Text strong style={{ color: colorError, fontSize: 40 }}>
                                {statisticsInfo?.postsStatistics.plannedPosts}
                            </Text>
                        </Flex>
                    </Flex>
                    <Flex gap={8} justify={'space-between'} style={{ marginBottom: 25 }}>
                        <Flex
                            style={{
                                backgroundColor: colorHighlight,
                                borderRadius: '8px',
                                width: '100%',
                                position: 'relative'
                            }}
                            vertical
                            align="center"
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: -10,
                                    border: '1px solid #7C9BAD',
                                    backgroundColor: 'white',
                                    borderRadius: 5,
                                    padding: 5,
                                    display: 'flex'
                                }}
                            >
                                <DollarOutlined style={{ fontSize: 20, color: '#7C9BAD' }} />
                            </div>
                            <Title style={{ marginTop: 25, marginBottom: 0 }} level={5}>
                                Продано
                            </Title>
                            <Text strong style={{ color: colorSuccess, fontSize: 40 }}>
                                {statisticsInfo?.postsStatistics.soldPosts}
                            </Text>
                        </Flex>
                        <Flex
                            style={{
                                backgroundColor: colorHighlight,
                                borderRadius: '8px',
                                width: '100%',
                                position: 'relative'
                            }}
                            vertical
                            align="center"
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: -10,
                                    border: '1px solid #7C9BAD',
                                    backgroundColor: 'white',
                                    borderRadius: 5,
                                    padding: 5,
                                    display: 'flex'
                                }}
                            >
                                <WalletOutlined style={{ fontSize: 20, color: '#7C9BAD' }} />
                            </div>
                            <Title style={{ marginTop: 25, marginBottom: 0 }} level={5}>
                                Куплено
                            </Title>
                            <Text strong style={{ color: colorError, fontSize: 40 }}>
                                {statisticsInfo?.postsStatistics.boughtPosts}
                            </Text>
                        </Flex>
                    </Flex>
                    <Flex vertical gap={10}>
                        <HighchartsReact highcharts={Highcharts} options={options} />

                        <HighchartsReact highcharts={Highcharts} options={opn} />

                        <PostViews averageViews={statisticsInfo?.postsStatistics.averageViews} />

                        <Involvement involvementEr={statisticsInfo?.postsStatistics.involvementEr} />

                        <HorizontalBar topPosts={statisticsInfo?.topPosts} />
                    </Flex>
                </Card>
                <BottomNavMobile />
            </>
        )
    }

    return (
        <Card>
            <Flex justify="space-between" align="center" style={{ marginBottom: '16px' }}>
                <Title level={3}>Статистика</Title>
            </Flex>
            <Space size={20} align="start">
                <Avatar shape="circle" size={100} src={current?.photoUrl} />
                <Flex vertical={true} gap={6}>
                    <Title
                        level={4}
                        style={{ margin: 0, cursor: 'pointer' }}
                        onClick={() => window.open(`https://t.me/${current?.username}`)}
                    >
                        <Space>
                            {current?.name}
                            <ExportOutlined />
                        </Space>
                    </Title>

                    <Text italic>@{current?.username}</Text>
                    <Text>Правообладатель: @{channelDetails?.owner}</Text>
                    <Space size={2}>
                        <Text>{current?.timezone}</Text>
                    </Space>
                    <Text>{current?.membersCount} подписчиков</Text>
                    <Text>{categories.find((c) => c.value === current?.category)?.label}</Text>
                </Flex>
            </Space>
            <Divider />
            <div style={{ maxHeight: 'calc(100vh - 380px)', overflow: 'auto' }}>
                <Flex style={{ marginBottom: '24px' }}>
                    <Radio.Group
                        onChange={(value) => setDisplay(value.target.value)}
                        value={display}
                        defaultValue={display}
                    >
                        <Radio.Button
                            value={EDisplayPosts.day}
                            onClick={() => {
                                setDates({
                                    startDate: dayjs().startOf('day').toDate(),
                                    endDate: dayjs().endOf('day').toDate()
                                })
                            }}
                        >
                            {EDisplayPosts.day}
                        </Radio.Button>
                        <Radio.Button
                            value={EDisplayPosts.week}
                            onClick={() => {
                                setDates({
                                    startDate: dayjs().startOf('week').toDate(),
                                    endDate: dayjs().endOf('week').toDate()
                                })
                            }}
                        >
                            {EDisplayPosts.week}
                        </Radio.Button>
                        <Radio.Button
                            value={EDisplayPosts.month}
                            onClick={() => {
                                setDates({
                                    startDate: dayjs().startOf('month').toDate(),
                                    endDate: dayjs().endOf('month').toDate()
                                })
                            }}
                        >
                            {EDisplayPosts.month}
                        </Radio.Button>
                        <Popover
                            content={periodPicker}
                            trigger={'click'}
                            open={isPeriodVisible}
                            placement="bottomRight"
                        >
                            <Radio.Button value={EDisplayPosts.all} onClick={() => setIsPeriodVisible(true)}>
                                Период
                            </Radio.Button>
                        </Popover>
                    </Radio.Group>
                </Flex>
                <Row gutter={16}>
                    <Col span={4}>
                        <Flex style={{ backgroundColor: colorHighlight, borderRadius: '8px' }} vertical align="center">
                            <Title level={5}>Всего опубликовано</Title>
                            <Text strong style={{ fontSize: 40 }}>
                                {statisticsInfo?.postsStatistics?.publishedPosts}
                            </Text>
                        </Flex>
                    </Col>
                    {/* <Col span={4}>
                        <Flex style={{ backgroundColor: colorHighlight, borderRadius: '8px' }} vertical align="center">
                            <Title level={5}>Партнерские связки</Title>
                            <Text strong style={{ color: colorSuccess, fontSize: 40 }}>
                                {statisticsInfo?.postsStatistics?.relationsCount}
                            </Text>
                        </Flex>
                    </Col> */}
                    <Col span={4}>
                        <Flex style={{ backgroundColor: colorHighlight, borderRadius: '8px' }} vertical align="center">
                            <Title level={5}>Продано</Title>
                            <Text strong style={{ color: colorSuccess, fontSize: 40 }}>
                                {statisticsInfo?.postsStatistics.soldPosts}
                            </Text>
                        </Flex>
                    </Col>
                    <Col span={4}>
                        <Flex style={{ backgroundColor: colorHighlight, borderRadius: '8px' }} vertical align="center">
                            <Title level={5}>Куплено</Title>
                            <Text strong style={{ color: colorError, fontSize: 40 }}>
                                {statisticsInfo?.postsStatistics.boughtPosts}
                            </Text>
                        </Flex>
                    </Col>
                    <Col span={4}>
                        <Flex style={{ backgroundColor: colorHighlight, borderRadius: '8px' }} vertical align="center">
                            <Title level={5}>Запланировано постов</Title>
                            <Text strong style={{ fontSize: 40 }}>
                                {statisticsInfo?.postsStatistics.plannedPosts}
                            </Text>
                        </Flex>
                    </Col>
                </Row>
                <Divider />
                <Row gutter={[20, 20]} style={{ marginBottom: 20 }}>
                    <Col span={12}>
                        <HighchartsReact highcharts={Highcharts} options={options} />
                    </Col>
                    <Col span={12}>
                        <HighchartsReact highcharts={Highcharts} options={opn} />
                    </Col>
                </Row>

                <Row gutter={[20, 20]} style={{ marginBottom: 20 }}>
                    <Col span={12}>
                        <PostViews averageViews={statisticsInfo?.postsStatistics.averageViews} />
                    </Col>
                    <Col span={12}>
                        <Involvement involvementEr={statisticsInfo?.postsStatistics.involvementEr} />
                    </Col>
                </Row>

                <Row gutter={[20, 20]}>
                    <Col span={12}>
                        {' '}
                        <HorizontalBar topPosts={statisticsInfo?.topPosts} />
                    </Col>
                </Row>
            </div>
        </Card>
    )
}
